import React, { useState, useRef, useCallback, useContext, useEffect } from "react";
import { CustomError } from "../../../utils/customeError";
import { ErrorCallback, ProgressCallback, SKMediaType, SuccessCallback, uploadFile } from "../../../api/shared/uploadService";
import { UploadTask } from "firebase/storage";
import * as Yup from "yup";
import { CreateArtifactContext } from "../../../context/CreateArtifactContext";
import { ContentState } from "../../../models/SKArtifact";
import { isEditable } from "@testing-library/user-event/dist/utils";

interface DocumentInputProps {
  onFileChange: (url: string | null) => void;
  documentUrl?: string;
  maxFileSize?: number;
  docId: string;
  editable?: boolean;
  folder: string;
}

const DocumentInput = ({ docId, onFileChange, documentUrl, maxFileSize = 10, editable = true, folder }: DocumentInputProps) => {
  const { contentData, artifactId, updateContentData } = useContext(CreateArtifactContext);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [documentTask, setDocumentTask] = useState<UploadTask | null>(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [previewStyle, setPreviewStyle] = useState<React.CSSProperties>({
    width: "150px",
    height: "150px",
  });

  const documentSchema = Yup.object().shape({
    documentFile: Yup.mixed<File>().test("fileSize", `File Size should be below ${maxFileSize} MB`, value => {
      if (!value) return false;
      const fileSize = value.size;
      return fileSize <= maxFileSize * 1024 * 1024;
    }),
  });

  const progressCallback: ProgressCallback = (progress: number, mediaType: SKMediaType) => {
    console.log(`Upload progress: ${progress}%`);
  };

  const errorCallback: ErrorCallback = (error: Error, mediaType: SKMediaType) => {
    console.error(`Upload failed: ${error.message}`);
    setUploadingDocument(false);
  };

  const successCallback = useCallback(
    (url: string, mediaType: SKMediaType) => {
      setUploadingDocument(false);
      onFileChange(url);
    },
    [onFileChange]
  );

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      try {
        const fileUrl = URL.createObjectURL(file);
        await documentSchema.validate({ documentFile: file }, { abortEarly: false });
        setErrorMessage("");
        setFilePreview(fileUrl);
        setUploadingDocument(true);
        let task = uploadFile(file, docId, SKMediaType.document, progressCallback, errorCallback, successCallback, folder);
        setDocumentTask(task);

        // Set the height and width while maintaining the aspect ratio
        const img = new Image();
        img.src = fileUrl;
        img.onload = () => {
          const { width, height } = img;
          const aspectRatio = width / height;
          let newWidth = Math.min(290, width);
          let newHeight = newWidth / aspectRatio;
          if (newHeight > 150) {
            newHeight = 150;
            newWidth = newHeight * aspectRatio;
          }
          setPreviewStyle({
            width: `${newWidth}px`,
            height: `${newHeight}px`,
          });
        };
      } catch (err) {
        let error = err as Error;
        setErrorMessage(error.message);
        setUploadingDocument(false);
      }
    },
    [successCallback, docId, documentSchema]
  );

  const removeFile = () => {
    onFileChange(null);
  };

  return (
    <div className="flex flex-col" style={{ width: previewStyle.width }}>
      <div
        className={`flex relative items-center justify-center border-2 border-gray-300 max-w-[290px] max-h-[150px] overflow-hidden rounded-lg ${
          editable ? "cursor-pointer" : ""
        }`}
        onClick={() => {
          editable && fileInputRef?.current?.click();
        }}
        style={previewStyle}
      >
        {editable && !uploadingDocument && documentUrl && contentData.status !== ContentState.IN_REVIEW && (
          <button
            className="absolute top-2 right-2"
            onClick={e => {
              e.stopPropagation();
              removeFile();
            }}
          >
            <img src="/icons/CloseButton.png" alt="remove" />
          </button>
        )}
        <input
          name="documentFile"
          type="file"
          ref={fileInputRef}
          accept=".pdf,.doc,.docx,.ppt,.pptx"
          onChange={handleFileChange}
          className="hidden"
          id={`file-upload-document`}
          disabled={!editable}
        />

        {uploadingDocument && (
          <div className="flex flex-col items-center justify-center p-4 space-y-4 bg-white shadow-sm w-44 h-44 rounded-2xl">
            <img src={"/icons/mediaupload.gif"} alt="Saving" className="w-[25px] h-[25px]" />
            <div className="font-bold text-center text-gray-400 w-[69px] h-[16px]">Uploading...</div>
            <button
              className="text-black border border-gray-400 rounded-md w-[53px] h-[30px]"
              onClick={e => {
                e.stopPropagation();
                setFilePreview(null);
                setUploadingDocument(false);
                documentTask && documentTask.cancel();
              }}
            >
              Cancel
            </button>
          </div>
        )}
        {!uploadingDocument && documentUrl && (
          <div className="w-full h-full overflow-hidden">
            <iframe src={documentUrl} width="100%" height="200px" title="Document Preview"></iframe>
          </div>
        )}
        {!(uploadingDocument || documentUrl) && (
          <label htmlFor="file-upload" className="m-4 cursor-pointer">
            <div className="flex flex-col items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 mb-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
              </svg>
              <span className="w-2/3 text-xs text-center text-skgray">PDF</span>
            </div>
          </label>
        )}
      </div>
      {errorMessage && <p className="mt-2 text-sm text-red-500">{errorMessage}</p>}
    </div>
  );
};

export default DocumentInput;
